interface ErrorReport {
  error_type: 'frontend';
  severity: 'critical' | 'error' | 'warning' | 'info';
  message: string;
  stack_trace?: string;
  error_code?: string;
  request_path?: string;
  request_method?: string;
  status_code?: number;
  user_id?: string;
  user_agent?: string;
  session_id?: string;
  environment?: Record<string, any>;
  metadata?: Record<string, any>;
}

class ErrorReportingService {
  private errorServiceUrl: string;
  private enabled: boolean = true;
  private rateLimitCount: number = 0;
  private rateLimitWindow: number = 60000; // 1 minute
  private maxErrorsPerWindow: number = 10;

  constructor() {
    // In production, error service is accessible at /log (same origin, proxied by nginx)
    // In development, use VITE_ERROR_SERVICE_URL if specified
    this.errorServiceUrl = import.meta.env.VITE_ERROR_SERVICE_URL || `${window.location.origin}/log`;

    // Reset rate limit counter every minute
    setInterval(() => {
      this.rateLimitCount = 0;
    }, this.rateLimitWindow);
  }

  /**
   * Report an error to the error ingestion service
   */
  async reportError(
    message: string,
    options: {
      severity?: 'critical' | 'error' | 'warning' | 'info';
      stack?: string;
      errorCode?: string;
      metadata?: Record<string, any>;
    } = {}
  ): Promise<void> {
    if (!this.enabled) {
      console.debug('Error reporting disabled');
      return;
    }

    // Rate limiting
    if (this.rateLimitCount >= this.maxErrorsPerWindow) {
      console.warn('Error reporting rate limit exceeded');
      return;
    }

    this.rateLimitCount++;

    try {
      const user = this.getAuthenticatedUser();
      const environment = this.getEnvironment();

      const errorReport: ErrorReport = {
        error_type: 'frontend',
        severity: options.severity || 'error',
        message: this.sanitizeMessage(message),
        stack_trace: options.stack,
        error_code: options.errorCode,
        request_path: window.location.pathname,
        user_id: user?.id,
        user_agent: navigator.userAgent,
        session_id: this.getSessionId(),
        environment,
        metadata: {
          ...options.metadata,
          url: window.location.href,
          referrer: document.referrer,
          timestamp: new Date().toISOString(),
        },
      };

      // Send to error service (fire and forget)
      this.sendError(errorReport);
    } catch (error) {
      console.error('Failed to report error:', error);
    }
  }

  /**
   * Report an API error
   */
  async reportApiError(
    message: string,
    statusCode: number,
    options: {
      method?: string;
      path?: string;
      requestBody?: any;
      responseBody?: any;
      metadata?: Record<string, any>;
    } = {}
  ): Promise<void> {
    await this.reportError(message, {
      severity: statusCode >= 500 ? 'error' : 'warning',
      errorCode: `HTTP_${statusCode}`,
      metadata: {
        ...options.metadata,
        status_code: statusCode,
        request_method: options.method,
        request_path: options.path,
        request_body: this.sanitizeData(options.requestBody),
        response_body: this.sanitizeData(options.responseBody),
      },
    });
  }

  /**
   * Get authenticated user from localStorage/cookies
   */
  private getAuthenticatedUser(): { id: string } | null {
    try {
      // Try to get from localStorage (adjust based on your auth implementation)
      const userStr = localStorage.getItem('user') || sessionStorage.getItem('user');
      if (userStr) {
        const user = JSON.parse(userStr);
        return user?.id ? { id: user.id } : null;
      }
    } catch (e) {
      // Ignore parsing errors
    }
    return null;
  }

  /**
   * Get browser environment information
   */
  private getEnvironment(): Record<string, any> {
    return {
      browser: this.getBrowserName(),
      browser_version: this.getBrowserVersion(),
      os: this.getOS(),
      viewport: `${window.innerWidth}x${window.innerHeight}`,
      screen: `${window.screen.width}x${window.screen.height}`,
      language: navigator.language,
      online: navigator.onLine,
      cookie_enabled: navigator.cookieEnabled,
    };
  }

  /**
   * Get or create session ID
   */
  private getSessionId(): string {
    let sessionId = sessionStorage.getItem('error_session_id');
    if (!sessionId) {
      sessionId = `sess_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      sessionStorage.setItem('error_session_id', sessionId);
    }
    return sessionId;
  }

  /**
   * Send error to service (async, fire-and-forget)
   */
  private async sendError(errorReport: ErrorReport): Promise<void> {
    try {
      const response = await fetch(this.errorServiceUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(errorReport),
        credentials: 'omit',
      });

      if (!response.ok) {
        console.warn(`Error service returned ${response.status}`);
      }
    } catch (error) {
      // Fail silently - don't disrupt user experience
      console.debug('Failed to send error to service:', error);
    }
  }

  /**
   * Sanitize error message to remove sensitive data
   */
  private sanitizeMessage(message: string): string {
    // Remove potential tokens, API keys, etc.
    return message
      .replace(/([a-zA-Z0-9_-]{20,})/g, '[REDACTED_TOKEN]')
      .replace(/Bearer\s+[\w-]+/gi, 'Bearer [REDACTED]')
      .substring(0, 10000); // Limit length
  }

  /**
   * Sanitize data objects
   */
  private sanitizeData(data: any): any {
    if (!data) return undefined;

    const sanitized = JSON.parse(JSON.stringify(data));

    // Remove sensitive fields
    const sensitiveKeys = ['password', 'token', 'secret', 'apiKey', 'api_key', 'authorization'];

    const removeKeys = (obj: any) => {
      if (typeof obj !== 'object' || obj === null) return;

      for (const key in obj) {
        if (sensitiveKeys.some((k) => key.toLowerCase().includes(k))) {
          obj[key] = '[REDACTED]';
        } else if (typeof obj[key] === 'object') {
          removeKeys(obj[key]);
        }
      }
    };

    removeKeys(sanitized);
    return sanitized;
  }

  /**
   * Get browser name
   */
  private getBrowserName(): string {
    const ua = navigator.userAgent;
    if (ua.includes('Firefox')) return 'Firefox';
    if (ua.includes('Chrome')) return 'Chrome';
    if (ua.includes('Safari')) return 'Safari';
    if (ua.includes('Edge')) return 'Edge';
    return 'Unknown';
  }

  /**
   * Get browser version
   */
  private getBrowserVersion(): string {
    const ua = navigator.userAgent;
    const match = ua.match(/(Firefox|Chrome|Safari|Edge)\/([0-9.]+)/);
    return match ? match[2] : 'Unknown';
  }

  /**
   * Get operating system
   */
  private getOS(): string {
    const ua = navigator.userAgent;
    if (ua.includes('Win')) return 'Windows';
    if (ua.includes('Mac')) return 'macOS';
    if (ua.includes('Linux')) return 'Linux';
    if (ua.includes('Android')) return 'Android';
    if (ua.includes('iOS')) return 'iOS';
    return 'Unknown';
  }

  /**
   * Enable error reporting
   */
  enable(): void {
    this.enabled = true;
  }

  /**
   * Disable error reporting
   */
  disable(): void {
    this.enabled = false;
  }
}

// Export singleton instance
export const errorReportingService = new ErrorReportingService();
